import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { spanPlaybackBounds, type TimelineSpan } from "@/data/join-chapters";
import type { CompiledChapter } from "@/data/schema/compiled";
import type { FitCastBoardContext } from "@/lib/timeline/board/FitCast/context";
import type { BookJourneyModel } from "@/pages/loadBookTimeline";
import { BookTimelineViewStage } from "./BookTimelineViewStage";
import { useRememberLoadingChapter } from "./loadingChapter";
import { useJourneySwitch } from "./useJourneySwitch";
import { useUniverseJourney } from "./useUniverseJourney";
import { useBookTimelinePlayback } from "./useBookTimelinePlayback";
import { useSelectedChapters } from "./useSelectedChapters";
import { useTimelineSpan } from "./useTimelineSpan";
import { useBookTimelineStage } from "./useBookTimelineStage";
import {
  readTimelineSessionRestore,
  useTimelineSessionPersist,
} from "./useTimelineSessionPersist";
import { useTimelineFrameLoop } from "./useTimelineFrameLoop";
import { useTimelineGpuCanvas } from "./useTimelineGpuCanvas";
import { useBookTimelineBoardDebug } from "./useBookTimelineBoardDebug";
import { useTimelineUiPrefs } from "./useTimelineUiPrefs";
import { useTimelinePlaybackShortcuts } from "./useTimelinePlaybackShortcuts";
import { useTimelineViewRecovery } from "./useTimelineViewRecovery";
import { usePlayerAddressBar } from "./usePlayerAddressBar";
import { useResetHomeOnSpanJump } from "./useResetHomeOnSpanJump";

interface BookTimelineViewProps {
  chapters: CompiledChapter[];
  spans: TimelineSpan[];
  initialSpanId?: string;
  loadUniverse?: () => Promise<BookJourneyModel>;
  initialUniverse?: BookJourneyModel;
}

export function BookTimelineView({
  chapters,
  spans,
  initialSpanId,
  loadUniverse,
  initialUniverse,
}: BookTimelineViewProps) {
  const { uiPrefs, updateUiPrefs } = useTimelineUiPrefs();
  const span = useTimelineSpan(spans, initialSpanId);
  const selected = useSelectedChapters(chapters, spans, span.spanId);
  const { featureFlags, boardDebugActive } = useBookTimelineBoardDebug(
    uiPrefs.boardModelDebug,
  );
  const journeyView = useUniverseJourney(
    selected,
    featureFlags.universeJourneyView,
    loadUniverse,
    initialUniverse,
    uiPrefs.spoilerLevel === "shown",
  );
  const { journey, active, layout, loading } = journeyView;
  const sessionRestore = useMemo(
    () => readTimelineSessionRestore(active.chapter),
    [active.chapter.bookId, active.chapter.chapterId],
  );
  const playback = useBookTimelinePlayback(
    spanPlaybackBounds(active.spans[0]),
    sessionRestore
      ? { playheadMs: sessionRestore.playheadMs, speed: sessionRestore.speed }
      : undefined,
    layout.beats,
    journeyView.hasPendingScene(),
    uiPrefs.repeatEnabled,
  );
  useEffect(() => {
    updateUiPrefs({ journey, spanId: span.spanId, repeatEnabled: playback.repeat });
  }, [journey, span.spanId, playback.repeat, updateUiPrefs]);
  usePlayerAddressBar({
    journey,
    spanId: span.spanId,
    cameraLock: uiPrefs.cameraLockEnabled,
    autoZoom: uiPrefs.autoZoomEnabled,
  });
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [canvasEl, setCanvasEl] = useState<HTMLCanvasElement | null>(null);
  const boardFitRef = useRef<FitCastBoardContext | null>(null);
  const getBoardFitContext = useCallback(() => boardFitRef.current, []);
  const board = active.board;

  const stage = useBookTimelineStage(
    layout,
    uiPrefs.cameraLockEnabled,
    uiPrefs.autoZoomEnabled,
    () => updateUiPrefs({ autoZoomEnabled: false }),
    playback.getPlayback,
    sessionRestore?.view ?? null,
    () => updateUiPrefs({ cameraLockEnabled: false, autoZoomEnabled: false }),
    getBoardFitContext,
  );

  useTimelinePlaybackShortcuts(playback.togglePlay, playback.setScrub);
  useTimelineSessionPersist(
    active.chapter, playback.getPlayback, stage.getCameraFrame,
  );
  const { onJourneyChange, onSpanIdChange } = useJourneySwitch(
    journeyView,
    playback,
    span.setSpanId,
    uiPrefs.cameraLockEnabled ? undefined : stage.resetToHome,
  );
  useRememberLoadingChapter(layout, playback.getPlayback);

  useTimelineViewRecovery({
    ready: canvasEl !== null && board !== undefined,
    stage,
    uiPrefs,
    updateUiPrefs,
  });
  useTimelineFrameLoop(canvasEl, {
    onTick: playback.onTick,
    getPlayback: playback.getPlayback,
    beforeFrame: uiPrefs.cameraLockEnabled
      ? stage.resolveViewTransform
      : undefined,
    getBoardFrame: board
      ? () => ({ board, viewportWidth: stage.getViewportWidth() })
      : undefined,
    boardFitRef: board ? boardFitRef : undefined,
  });
  useResetHomeOnSpanJump(playback.spanJump, uiPrefs.cameraLockEnabled, stage.resetToHome);

  const { canDraw } = useTimelineGpuCanvas(canvasEl, {
    layout,
    chapterBoard: board,
    getPlayback: playback.getPlayback,
    getViewportWidth: stage.getViewportWidth,
    getViewTransform: stage.getViewTransform,
    resolveViewTransform: uiPrefs.cameraLockEnabled
      ? stage.resolveViewTransform
      : undefined,
  });

  return (
    <BookTimelineViewStage
      stage={stage}
      stageClassName={[
        "timeline-stage",
        boardDebugActive ? "timeline-stage--board-debug" : "",
      ].filter(Boolean).join(" ")}
      canDraw={canDraw}
      canvasEl={canvasEl}
      setCanvasEl={setCanvasEl}
      canvasRef={canvasRef}
      layout={layout}
      chapterBoard={board}
      boardDebugActive={boardDebugActive}
      getPlayback={playback.getPlayback}
      uiPrefs={uiPrefs}
      updateUiPrefs={updateUiPrefs}
      playback={playback}
      spans={journey === "universe" ? [] : spans}
      spanId={span.spanId}
      onSpanIdChange={onSpanIdChange}
      featureFlags={featureFlags}
      journey={journey}
      journeyLoading={loading}
      journeyFailed={journeyView.failed}
      onJourneyChange={onJourneyChange}
      onJourneyPrefetch={journeyView.prefetch}
    />
  );
}
